import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaymentDelays, useReviewPaymentDelay } from '@saiens/shared/hooks';
import { formatDate } from '@saiens/shared/utils';
import type { PaymentDelayItem } from '@saiens/shared/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Check, X } from 'lucide-react';

export function PaymentDelayApprovalsPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = usePaymentDelays({ limit: 50 });
  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-lg">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate('/approval')} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">延後付款審核</h1>
      </div>

      <div className="space-y-4 px-4 py-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">目前沒有待審核的延後付款申請</div>
        ) : (
          items.map((item) => <DelayCard key={item.id} item={item} />)
        )}
      </div>
    </div>
  );
}

function DelayCard({ item }: { item: PaymentDelayItem }) {
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const reviewMutation = useReviewPaymentDelay();

  const handleReview = (action: 'approve' | 'reject') => {
    reviewMutation.mutate({ id: item.id, action, note: note.trim() || undefined });
  };

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm">
      <div className="mb-2">
        <p className="font-medium text-gray-900">
          {item.project_name || item.sale_order_name || item.name}
        </p>
        <p className="text-sm text-gray-500">{item.partner_name || '---'}</p>
      </div>

      <div className="mb-3 space-y-1 text-sm text-gray-600">
        <div className="flex justify-between">
          <span className="text-gray-400">尾款金額</span>
          <span className="font-medium">NT${item.balance_amount?.toLocaleString() ?? '---'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">申請人</span>
          <span>{item.payment_delay_requested_by_name || '---'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">申請日期</span>
          <span>{item.payment_delay_requested_date ? formatDate(item.payment_delay_requested_date) : '---'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">希望延後至</span>
          <span className="font-medium text-amber-600">
            {item.payment_delay_date ? formatDate(item.payment_delay_date) : '其他（見說明）'}
          </span>
        </div>
      </div>

      <div className="mb-3 rounded-lg bg-gray-50 p-3">
        <p className="mb-1 text-xs font-medium text-gray-400">延後原因</p>
        <p className="text-sm text-gray-700">{item.payment_delay_reason || '未填寫'}</p>
      </div>

      {showNote && (
        <div className="mb-3">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="審核備註（選填）"
            className="w-full rounded-lg border border-gray-200 p-2.5 text-sm focus:border-primary focus:outline-none"
          />
        </div>
      )}

      {!showNote && (
        <button
          onClick={() => setShowNote(true)}
          className="mb-3 text-xs text-gray-400 hover:text-gray-600"
        >
          + 新增備註
        </button>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => handleReview('reject')}
          disabled={reviewMutation.isPending}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
          駁回
        </button>
        <button
          onClick={() => handleReview('approve')}
          disabled={reviewMutation.isPending}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
          核准
        </button>
      </div>
    </div>
  );
}
